/**
 * Confirmation shown when a user acting as a VAU tries to switch
 * org/product/instance from the Opti ID global bar. We can't navigate them
 * away while impersonating (they'd get stuck under the VAU identity), so we
 * ask first, then release the VAU before continuing to the destination.
 *
 * Controlled by `useVauSwitchGuard` — shown whenever a `target` is pending.
 * There is no trigger; it is opened programmatically by the global bar's
 * change event.
 */

import { Text } from '@optiaxiom/react';

import { ConfirmationModal } from '@frontend/components/confirmationModal/components/ConfirmationModal';

import type { GlobalBarSwitchTarget } from '../hooks/resolveGlobalBarSwitchTarget';

type VauSwitchConfirmDialogProps = {
  target: GlobalBarSwitchTarget | null;
  vauName?: string;
  principalEmail?: string;
  isReleasing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function VauSwitchConfirmDialog({
  target,
  vauName,
  principalEmail,
  isReleasing,
  onConfirm,
  onCancel,
}: VauSwitchConfirmDialogProps) {
  const actingAs = vauName ?? 'a VAU';

  return (
    <ConfirmationModal
      isShown={!!target}
      title="Switch back to your profile?"
      action="Switch back & continue"
      cancelLabel="Stay as this VAU"
      confirmLoading={isReleasing}
      actionDisabled={isReleasing}
      cancelDisabled={isReleasing}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Text>
        You&apos;re currently acting as <strong>{actingAs}</strong>. To open{' '}
        <strong>{target?.label}</strong>, Opal will switch you back to your own profile
        {principalEmail ? ` (${principalEmail})` : ''} first — you can&apos;t stay in VAU mode
        across workspaces. Actions in the new workspace are recorded as you, not the VAU.
      </Text>
    </ConfirmationModal>
  );
}
